// @flow
// -----------------------------------------------------------------
// <copyright                                                       
//   file="Izenda.RequestBuilderBody.js"                            
//   company="Izenda">                                              
//  Copyright (c) 2015 Izenda, Inc.                                 
//  ALL RIGHTS RESERVED                                             
//                                                                  
//  The entire contents of this file is protected by U.S. and       
//  International Copyright Laws. Unauthorized reproduction,        
//  reverse-engineering, and distribution of all or any portion of  
//  the code contained in this file is strictly prohibited and may  
//  result in severe civil and criminal penalties and will be       
//  prosecuted to the maximum extent possible under the law.        
//                                                                  
//  RESTRICTIONS                                                    
//                                                                  
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES           
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE                          
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS              
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET    
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.                 
//                                                                  
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED      
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE        
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE       
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT  
//  AND PERMISSION FROM IZENDA INC.                                 
//                                                                  
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  
//  ADDITIONAL RESTRICTIONS.                                        
// </copyright>                                                     
// -----------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { action } from 'mobx';
import { Controlled as CodeMirror } from 'react-codemirror2'
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/xml/xml';
import 'codemirror/mode/markdown/markdown';
import 'codemirror/addon/fold/foldgutter';

import Panel from 'share/components/controls/Izenda.Panel';
import localizer from 'share/localization/Izenda.Localizer';
import IzendaRequest, { IZENDA_REQUEST_BODY_LANGUAGES } from 'logic/models/Izenda.Request';
import connect from 'share/enhancers/Izenda.Connect';
import DropDown from 'share/components/controls/Izenda.DropDown';

/**
 * Panel header component
 */
const RequestBuilderBodyPanelHeader = connect()(
  ({ title, icon, className, collapseToggle, style }) => {
    return (
      <div>
        <strong onClick={collapseToggle} style={style} className={className}>
          {icon ? <i className={icon}/> : null}
          {title}
        </strong>
      </div>
    );
  });

/**
 * Request builder body editor component.
 */
@connect()
export default class RequestBuilderBody extends Component {
  static propTypes = {
    request: PropTypes.any.isRequired // IzendaRequest
  };

  state = {
    requestBodyText: null,
    requestBodyOptions: null,
  };

  constructor(props) {
    super(props);
    this.state.requestBodyText = props.request.body;
    this.state.requestBodyOptions = this.getCodeMirrorOptions(props.request);
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      requestBodyText: nextProps.request.body,
      requestBodyOptions: this.getCodeMirrorOptions(nextProps.request)
    });
  }

  /**
   * Get language for current request
   * @param {IzendaRequest} request
   * @returns {{mode: string, foldGutter: boolean, lineNumbers: boolean, gutters: [string, string], lineWrapping: boolean}}
   */
  getCodeMirrorOptions(request) {
    if (request.bodyLanguage === IZENDA_REQUEST_BODY_LANGUAGES.JSON) {
      return {
        mode: 'application/json',
        lineNumbers: true,
        lineWrapping: true,
        foldGutter: true,
        gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
      };
    }
    if (request.bodyLanguage === IZENDA_REQUEST_BODY_LANGUAGES.XML) {
      return {
        mode: 'application/xml',
        lineNumbers: true,
        lineWrapping: true,
        foldGutter: true,
        gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
      };
    }
    return {
      mode: 'text/plain',
      lineNumbers: true,
      lineWrapping: true,
      foldGutter: true,
      gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
    };
  }

  @action
  onUpdateRequestBodyLanguage = lang => {
    const { props } = this;
    const request: IzendaRequest = props.request;
    request.setBodyLanguage(lang.value);
    this.setState({
      requestBodyOptions: this.getCodeMirrorOptions(request)
    });
  };

  @action
  onUpdateRequestBody = (editor, data, newValue) => {
    const { props } = this;
    const request: IzendaRequest = props.request;
    request.setBody(newValue);
  };

  renderCodeFormatButton = () => {
    const { props } = this;
    const request: IzendaRequest = props.request;

    const languages = {
      TEXT: {
        value: IZENDA_REQUEST_BODY_LANGUAGES.TEXT,
        label: localizer.getText('COMMON_DATA_TYPE_TEXT')
      },
      JSON: {
        value: IZENDA_REQUEST_BODY_LANGUAGES.JSON,
        label: localizer.getText('COMMON_DATA_TYPE_JSON')
      },
      XML: {
        value: IZENDA_REQUEST_BODY_LANGUAGES.XML,
        label: localizer.getText('COMMON_DATA_TYPE_XML')
      }
    };

    return (
      <div className="u_marginBottom">
        <DropDown
          labelField='label'
          valueField='value'
          options={[languages.TEXT, languages.JSON, languages.XML]}
          value={request.bodyLanguage}
          onChange={this.onUpdateRequestBodyLanguage}
        />
      </div>
    );
  };

  render() {
    const { props } = this;
    const { requestBodyText, requestBodyOptions } = this.state;
    const request: IzendaRequest = props.request;
    const panelBodyProps = {
      title: localizer.getText('REQUEST_BUILDER_BODY_TITLE'),
      icon: 'icon-toggle-triangle',
      className: 'u_noPaddingLeft u-bgGray',
      request: request,
      component: RequestBuilderBodyPanelHeader
    };

    return (
      <Panel
        collapsible
        expanedOption
        header={panelBodyProps}
      >
        {this.renderCodeFormatButton()}
        <CodeMirror
          options={requestBodyOptions}
          value={requestBodyText}
          onChange={this.onUpdateRequestBody}
          onBeforeChange={(editor, data, value) => {
            this.setState({ requestBodyText: value });
          }}/>
      </Panel>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/ConnectionStringBuilderRest/Izenda.RequestBuilderBody.js